import { useRef } from "react";
import { cn } from "../lib/cn";

/** Resize a right-hand panel. Cancelled drags restore the starting width. */
export function PanelResizeHandle({
  value,
  min,
  max,
  onChange,
  label = "Resize panel",
  className,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label?: string;
  className?: string;
}) {
  const drag = useRef<{ x: number; width: number; pointer: number } | null>(null);
  const update = (next: number) => onChange(Math.min(max, Math.max(min, next)));
  const cancel = () => {
    if (!drag.current) return;
    const width = drag.current.width;
    drag.current = null;
    onChange(width);
  };
  return (
    <div
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={`${value} pixels`}
      title="Resize panel: arrow keys, Home or End; Escape cancels a drag"
      className={cn(
        "touch-none cursor-col-resize focus-visible:outline-none focus-visible:bg-focus",
        className,
      )}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary) return;
        event.preventDefault();
        event.currentTarget.focus();
        drag.current = { x: event.clientX, width: value, pointer: event.pointerId };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (drag.current?.pointer === event.pointerId)
          update(drag.current.width + drag.current.x - event.clientX);
      }}
      onPointerUp={(event) => {
        if (drag.current?.pointer !== event.pointerId) return;
        drag.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={cancel}
      onLostPointerCapture={cancel}
      onKeyDown={(event) => {
        if (event.key === "Escape" && drag.current) {
          event.preventDefault();
          event.stopPropagation();
          const pointer = drag.current.pointer;
          cancel();
          if (event.currentTarget.hasPointerCapture(pointer))
            event.currentTarget.releasePointerCapture(pointer);
          return;
        }
        if (drag.current) return;
        const step = event.shiftKey ? 8 : 32;
        const next =
          event.key === "ArrowLeft"
            ? value + step
            : event.key === "ArrowRight"
              ? value - step
              : event.key === "Home"
                ? min
                : event.key === "End"
                  ? max
                  : null;
        if (next === null) return;
        event.preventDefault();
        update(next);
      }}
    />
  );
}
